import {
  EventResponseControl,
  Responses,
  isRespondable,
} from "@clockwise/web-commons/src/ui/event-response-control/EventResponseControl";
import { TrackingEvents, useTracking } from "@clockwise/web-commons/src/util/analytics.util";
import React from "react";
import toast from "react-hot-toast";
import _useIsWebExtension from "../../util/useIsWebExtension";
import useEventDetails from "../web-app-calendar/hooks/useEventDetails";
import _useEventResponseStatus from "./hooks/useEventResponseStatus";

type Props = {
  calendarId: string;
  externalEventId: string;
  onUpdated?: () => void;
  options?: {
    useEventResponseStatus?: typeof _useEventResponseStatus;
    useIsWebExtension?: typeof _useIsWebExtension;
  };
};

export const EventResponseStatus = ({ calendarId, externalEventId, onUpdated, options }: Props) => {
  const useEventResponseStatus = options?.useEventResponseStatus ?? _useEventResponseStatus;
  const useIsWebExtension = options?.useIsWebExtension ?? _useIsWebExtension;

  const [selecting, setSelecting] = React.useState<Responses | null>(null);
  const track = useTracking();
  const { responseStatus, loading, update, updating } = useEventResponseStatus(
    externalEventId,
    calendarId,
  );
  const {
    eventDetails: { isCancelled },
  } = useEventDetails(externalEventId, calendarId);
  const isExtension = useIsWebExtension();

  // If the event is not respondable, don't render the response control.
  // This can happen if the event is not on the user's calendar.
  if (!isRespondable(responseStatus, isCancelled)) {
    return null;
  }

  const handleSelect = (value: Responses) => {
    if (!update) {
      toast.error("Failed to update meeting response");
      return;
    }

    setSelecting(value);

    void update({
      variables: { responseStatus: value },
      onCompleted: () => {
        setSelecting(null);
        track(TrackingEvents.EVENT.SCHEDULING.UPDATE_RESPONSE_STATUS, {
          responseStatus: value,
          source: isExtension ? "extension" : "planner",
        });
        toast.success("Response Updated!");
        onUpdated?.();
      },
      onError: () => {
        setSelecting(null);
        toast.error("Failed to update meeting response");
      },
    });
  };

  return (
    <EventResponseControl
      onSelect={handleSelect}
      selected={loading ? null : responseStatus}
      selecting={updating ? selecting : null}
    />
  );
};

export default EventResponseStatus;
